import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PowerOffOutlinedIcon from "@mui/icons-material/PowerOffOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { buildReferralAuthPath } from "../../../infrastructure/utils/referralAuthPaths";
import {
  resolveReferralCode,
  storeReferralFallback,
  trackReferralVisit,
} from "../../../infrastructure/utils/referralTracking";
import PanelInfo from "../../components/shared/panel-info";
import SolarentSiteFooter from "../../components/marketing/SolarentSiteFooter";

const SOLAR_GOLD = "#F6AE2D";
const SOLAR_ORANGE = "#FF9A3C";
const TEAL_DEEP = "#044F79";
const TEAL_MID = "#0A7EA4";

const HERO_HIGHLIGHTS = [
  "بررسی فنی و اقتصادی",
  "راهکار مقابله با خاموشی",
  "امکان فروش برق",
];

const OFFER_SUMMARY = [
  {
    label: "مناسب برای",
    value: "خانه، ویلا، باغ، سوله و کسب‌وکار",
    icon: HomeWorkOutlinedIcon,
  },
  {
    label: "ظرفیت پیشنهادی",
    value: "از ۵ کیلووات تا ظرفیت‌های بزرگ‌تر",
    icon: SolarPowerOutlinedIcon,
  },
  {
    label: "خروجی بررسی",
    value: "پیشنهاد فنی، اقتصادی و مسیر اجرا",
    icon: DescriptionOutlinedIcon,
  },
];

const BENEFITS = [
  {
    icon: BoltOutlinedIcon,
    title: "کاهش اثر خاموشی‌ها",
    body: "در راهکارهای هیبرید و مجهز به ذخیره‌ساز، تجهیزات ضروری شما می‌توانند هنگام قطعی برق به کار خود ادامه دهند.",
    accent: SOLAR_GOLD,
  },
  {
    icon: SavingsOutlinedIcon,
    title: "فرصت درآمد از تولید برق",
    body: "در طرح‌های متصل به شبکه، امکان فروش برق تولیدی و ایجاد جریان درآمدی از انرژی خورشیدی وجود دارد.",
    accent: SOLAR_ORANGE,
  },
  {
    icon: HomeWorkOutlinedIcon,
    title: "استفاده از فضای بلااستفاده",
    body: "پشت‌بام، باغ، سوله یا زمین شما می‌تواند به یک دارایی مولد و کاربردی تبدیل شود.",
    accent: TEAL_MID,
  },
  {
    icon: ShieldOutlinedIcon,
    title: "محافظت از تجهیزات",
    body: "طراحی صحیح سیستم می‌تواند ریسک آسیب ناشی از نوسان و قطعی برق را برای تجهیزات حساس کاهش دهد.",
    accent: TEAL_DEEP,
  },
];

const PLANT_SOLUTIONS = [
  {
    title: "آنگرید",
    icon: WbSunnyOutlinedIcon,
    bullets: [
      "مناسب فروش برق و کاهش هزینه انرژی",
      "بدون باتری",
      "وابسته به شبکه برق",
    ],
  },
  {
    title: "هیبرید",
    icon: BatteryChargingFullIcon,
    bullets: [
      "مناسب مقابله با خاموشی",
      "دارای ذخیره‌ساز باتری",
      "مناسب خانه، ویلا و کسب‌وکار",
    ],
    featured: true,
  },
  {
    title: "آفگرید",
    icon: PowerOffOutlinedIcon,
    bullets: [
      "مناسب مناطق فاقد برق یا قطعی شدید",
      "مستقل از شبکه",
      "نیازمند طراحی دقیق مصرف و باتری",
    ],
  },
];

const AUDIENCE = [
  "صاحبان خانه و ویلا",
  "صاحبان باغ و زمین",
  "صاحبان سوله و کارگاه",
  "فروشگاه‌ها و کسب‌وکارها",
  "واحدهای صنعتی و تولیدی",
];

const PROCESS_STEPS = [
  {
    title: "ثبت اطلاعات اولیه",
    body: "مشخصات تماس و اطلاعات محل احداث را وارد می‌کنید.",
    icon: AssignmentOutlinedIcon,
  },
  {
    title: "بررسی کارشناسان سولارنت",
    body: "شرایط فنی، فضای موجود و هدف شما بررسی می‌شود.",
    icon: HandymanOutlinedIcon,
  },
  {
    title: "دریافت پیشنهاد",
    body: "ظرفیت مناسب، نوع سیستم و مسیر اجرا پیشنهاد می‌شود.",
    icon: DescriptionOutlinedIcon,
  },
  {
    title: "شروع فرآیند اجرا",
    body: "در صورت تایید، مراحل تامین تجهیزات و اجرا آغاز می‌شود.",
    icon: PlayCircleOutlineIcon,
  },
];

const cardShadow = "0 8px 28px rgba(4, 79, 121, 0.08)";
const cardHoverShadow = "0 12px 36px rgba(4, 79, 121, 0.14)";
const SECTION_GAP = { xs: 4, sm: 4.5, md: 5 };
const GRID_SPACING = 2;

function SectionTitle({ children, subtitle }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="h6"
        fontWeight={800}
        sx={{ color: TEAL_DEEP, fontSize: { xs: "1.05rem", sm: "1.15rem" } }}
      >
        {children}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function LandingSection({ title, subtitle, children, sx }) {
  return (
    <Box sx={{ mt: SECTION_GAP, ...sx }}>
      {title ? <SectionTitle subtitle={subtitle}>{title}</SectionTitle> : null}
      <Box
        sx={{
          pt: title ? GRID_SPACING : 0,
          "& > .MuiGrid-container": {
            width: "100%",
            mx: 0,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function ReferralCtaButtons({
  referralCode,
  search,
  size = "large",
  contrast = false,
}) {
  const loginTo = buildReferralAuthPath("/login", referralCode, search);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ width: "100%", maxWidth: 520 }}
    >
      <Button
        component={Link}
        to={loginTo}
        variant="contained"
        size={size}
        fullWidth
        sx={{
          flex: 1,
          py: size === "large" ? 1.35 : 1,
          borderRadius: 2.5,
          fontWeight: 700,
          boxShadow: contrast ? "0 4px 16px rgba(0,0,0,0.15)" : `0 4px 14px ${alpha(SOLAR_GOLD, 0.45)}`,
          ...(contrast
            ? {
                bgcolor: "common.white",
                color: TEAL_DEEP,
                "&:hover": { bgcolor: "grey.100" },
              }
            : {
                bgcolor: SOLAR_GOLD,
                color: "#1a1a1a",
                "&:hover": { bgcolor: SOLAR_ORANGE },
              }),
        }}
      >
        درخواست بررسی شرایط احداث نیروگاه
      </Button>
      <Button
        component={Link}
        to={loginTo}
        variant="outlined"
        size={size}
        fullWidth
        sx={{
          flex: 1,
          py: size === "large" ? 1.2 : 1,
          borderRadius: 2.5,
          fontWeight: 600,
          ...(contrast
            ? {
                borderColor: "rgba(255,255,255,0.85)",
                color: "common.white",
                "&:hover": {
                  borderColor: "common.white",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }
            : {
                borderColor: alpha("#fff", 0.7),
                color: "common.white",
                bgcolor: alpha("#fff", 0.08),
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: alpha("#fff", 0.16),
                },
              }),
        }}
      >
        ورود به پنل کاربری
      </Button>
    </Stack>
  );
}

const ReferralLandingPage = () => {
  const theme = useTheme();
  const { referralCode: routeReferralCode } = useParams();
  const location = useLocation();
  const [trackingDone, setTrackingDone] = useState(false);

  const referralCode = useMemo(() => {
    const fromRoute = (routeReferralCode || "").trim();
    if (fromRoute) return fromRoute;
    return (
      resolveReferralCode({
        search: location.search,
        pathname: location.pathname,
        includeCookie: false,
      }) || ""
    );
  }, [routeReferralCode, location.search, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!referralCode) {
        setTrackingDone(true);
        return;
      }

      await trackReferralVisit({
        referralCode,
        search: location.search,
        pathname: location.pathname,
        landingUrl: window.location.href,
        includeCookie: false,
        force: true,
      });
      storeReferralFallback(referralCode);

      if (!cancelled) {
        setTrackingDone(true);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [referralCode, location.search, location.pathname]);

  const heroBg = `linear-gradient(155deg, ${TEAL_DEEP} 0%, ${TEAL_MID} 42%, ${alpha(
    SOLAR_GOLD,
    0.55
  )} 100%)`;

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f8fb",
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -10%, ${alpha(
          SOLAR_GOLD,
          0.12
        )}, transparent)`,
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          background: heroBg,
          color: "common.white",
          position: "relative",
          overflow: "hidden",
          pb: { xs: 10, sm: 11 },
          pt: { xs: 2, md: 2.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: -80,
            left: -60,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: alpha(SOLAR_GOLD, 0.2),
            filter: "blur(2px)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -40,
            right: -30,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: alpha("#fff", 0.06),
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                }}
              >
                <PanelInfo size={34} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ opacity: 0.95 }}>
                سولارنت
              </Typography>
            </Stack>
            {referralCode ? (
              <Chip
                size="small"
                label="معرفی‌شده توسط مشاور سولارنت"
                sx={{
                  bgcolor: alpha(SOLAR_GOLD, 0.95),
                  color: "#1a2a35",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 28,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              />
            ) : null}
          </Stack>

          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ px: { xs: 0.5, sm: 0 } }}>
            <Typography
              component="h1"
              fontWeight={800}
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.6rem" },
                lineHeight: 1.5,
                maxWidth: 620,
                textShadow: "0 1px 12px rgba(0,0,0,0.15)",
              }}
            >
              برق پایدار، آسایش بیشتر و فرصت درآمدزایی با نیروگاه خورشیدی
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: 580,
                lineHeight: 1.85,
                fontSize: { xs: "0.88rem", sm: "0.95rem" },
                opacity: 0.92,
              }}
            >
              اگر پشت‌بام، زمین، باغ، سوله یا فضای مناسبی در اختیار دارید، سولارنت
              شرایط احداث نیروگاه خورشیدی، تامین برق در زمان خاموشی و فرصت درآمد از
              تولید برق را برای شما بررسی می‌کند.
            </Typography>

            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
              spacing={0.75}
              sx={{ maxWidth: 520 }}
            >
              {HERO_HIGHLIGHTS.map((text) => (
                <Chip
                  key={text}
                  label={text}
                  size="small"
                  sx={{
                    bgcolor: alpha("#fff", 0.14),
                    color: "common.white",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 28,
                  }}
                />
              ))}
            </Stack>

            <ReferralCtaButtons
              referralCode={referralCode}
              search={location.search}
              size="large"
            />

            {!trackingDone ? (
              <Typography variant="caption" sx={{ opacity: 0.65 }}>
                در حال ثبت ارجاع…
              </Typography>
            ) : null}
          </Stack>
        </Container>
      </Box>

      {/* Offer summary — slight overlap with hero on larger screens only */}
      <Container
        maxWidth="md"
        sx={{
          position: "relative",
          zIndex: 2,
          mt: { xs: -4, sm: -6, md: -7 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 3, sm: 3.5 },
        }}
      >
        <Grid container spacing={1.5} sx={{ mx: 0, width: "100%" }}>
          {OFFER_SUMMARY.map((item) => {
            const Icon = item.icon;
            return (
              <Grid item xs={12} sm={4} key={item.label}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    boxShadow: cardShadow,
                    height: "100%",
                    border: "1px solid",
                    borderColor: alpha(TEAL_DEEP, 0.08),
                  }}
                >
                  <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: alpha(SOLAR_GOLD, 0.2),
                          color: TEAL_DEEP,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          display="block"
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} lineHeight={1.6}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <LandingSection
          title="چرا انرژی خورشیدی؟"
          subtitle="مزایای انرژی خورشیدی برای شما"
          sx={{ mt: { xs: 2, sm: 1 } }}
        >
          <Grid container spacing={GRID_SPACING}>
          {BENEFITS.map((item) => {
            const Icon = item.icon;
            return (
              <Grid item xs={12} sm={6} key={item.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: cardShadow,
                    borderTop: `3px solid ${item.accent}`,
                    transition: "box-shadow 0.2s, transform 0.2s",
                    position: "relative",
                    zIndex: 0,
                    "&:hover": {
                      boxShadow: cardHoverShadow,
                      transform: "translateY(-2px)",
                      zIndex: 1,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(item.accent, 0.15),
                          color: TEAL_DEEP,
                          flexShrink: 0,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
                          {item.body}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          </Grid>
        </LandingSection>

        <LandingSection title="کدام راهکار برای شما مناسب‌تر است؟">
          <Grid container spacing={GRID_SPACING}>
            {PLANT_SOLUTIONS.map((plant) => {
              const Icon = plant.icon;
              return (
                <Grid item xs={12} md={4} key={plant.title}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: plant.featured ? cardHoverShadow : cardShadow,
                      border: plant.featured
                        ? `2px solid ${alpha(SOLAR_GOLD, 0.7)}`
                        : `1px solid ${alpha(TEAL_DEEP, 0.1)}`,
                      bgcolor: plant.featured ? alpha(SOLAR_GOLD, 0.06) : "background.paper",
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Icon sx={{ color: TEAL_DEEP, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight={800} color={TEAL_DEEP}>
                          {plant.title}
                        </Typography>
                      </Stack>
                      <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25 }}>
                        {plant.bullets.map((line) => (
                          <Typography
                            key={line}
                            component="li"
                            variant="body2"
                            color="text.secondary"
                            lineHeight={1.7}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              lineHeight: 1.75,
            }}
          >
            انتخاب نهایی نوع نیروگاه پس از بررسی محل، مصرف، بودجه و هدف شما انجام
            می‌شود.
          </Typography>
        </LandingSection>

        <LandingSection title="این طرح برای چه کسانی مناسب است؟">
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {AUDIENCE.map((label) => (
              <Chip
                key={label}
                label={label}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  height: 32,
                  bgcolor: alpha(SOLAR_GOLD, 0.18),
                  color: TEAL_DEEP,
                  border: `1px solid ${alpha(SOLAR_GOLD, 0.45)}`,
                  "&:hover": { bgcolor: alpha(SOLAR_GOLD, 0.28) },
                }}
              />
            ))}
          </Stack>
        </LandingSection>

        <LandingSection title="مراحل شروع">
          <Grid container spacing={1.5}>
            {PROCESS_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={step.title}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2.5,
                      boxShadow: cardShadow,
                      border: "1px solid",
                      borderColor: alpha(TEAL_DEEP, 0.08),
                    }}
                  >
                    <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              bgcolor: TEAL_DEEP,
                              color: SOLAR_GOLD,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 800,
                              fontSize: "0.85rem",
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Icon sx={{ color: TEAL_MID, fontSize: 22 }} />
                        </Stack>
                        <Typography variant="subtitle2" fontWeight={800} color={TEAL_DEEP}>
                          {step.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" lineHeight={1.65}>
                          {step.body}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </LandingSection>

        <Card
          elevation={0}
          sx={{
            mt: SECTION_GAP,
            borderRadius: 3,
            boxShadow: cardShadow,
            borderRight: `4px solid ${SOLAR_GOLD}`,
            bgcolor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography variant="h6" fontWeight={800} color={TEAL_DEEP} gutterBottom>
              درخواست شما چگونه بررسی می‌شود؟
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.85}>
              پس از ثبت اطلاعات، کارشناسان سولارنت شرایط فنی و اقتصادی پروژه را
              بررسی می‌کنند و برای تکمیل اطلاعات یا اعلام پیشنهاد مناسب با شما تماس
              خواهند گرفت.
            </Typography>
          </CardContent>
        </Card>

        <Box
          sx={{
            mt: SECTION_GAP,
            mb: { xs: 1, md: 2 },
            borderRadius: 3,
            textAlign: "center",
            background: `linear-gradient(135deg, ${TEAL_DEEP} 0%, ${TEAL_MID} 55%, ${alpha(
              SOLAR_GOLD,
              0.85
            )} 100%)`,
            color: "common.white",
            boxShadow: "0 16px 48px rgba(4, 79, 121, 0.28)",
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          <Typography variant="h6" fontWeight={800} gutterBottom>
            آماده بررسی شرایط احداث نیروگاه خود هستید؟
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.92, mb: 2, maxWidth: 480, mx: "auto", lineHeight: 1.75 }}
          >
            اگر فضای مناسب در اختیار دارید، همین امروز شرایط احداث نیروگاه خود را
            بررسی کنید.
          </Typography>
          <Stack alignItems="center">
            <ReferralCtaButtons
              referralCode={referralCode}
              search={location.search}
              size="large"
              contrast
            />
          </Stack>
        </Box>
      </Container>

      <SolarentSiteFooter />
    </Box>
  );
};

export default ReferralLandingPage;
